import { InfoCircleFilled } from '@ant-design/icons'
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import { Tooltip } from 'antd'
import Checkbox from 'components/CustomCheckbox'
import InfoModal from 'components/Modals/InfoModal'
import MessageModal from 'components/Modals/MessageModal'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import { useUTMTracking } from 'hooks/useUTMTracking'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import swal from 'sweetalert'
import { getInvestorAccountType, getModifiedUrlWithStep } from 'utils'
import CompanyLogo from '../../../assets/afbc.png'
import Side2 from '../../../assets/new_side_10.jpg'
import Button from '../../../components/Button'
import LoaderModal from '../../../components/Modals/LoaderModal'
import Stepper from '../../../components/Stepper'
import { useRiseUpAPIV1 } from '../../../hooks/useRiseUpAPIV1'
import { IFormProps } from '../../../interfaces'

const infoText = {
  survivorship: {
    title: 'Joint Tenants with Right of Survivorship',
    description: `Please consult your lawyer or investment advisor as to the type of joint investment you need as many 
      jurisdictions have different rules, laws, and definitions of these terms. In general, in states that recognize 
      Joint Tenants with Right of Survivorship, the term can mean each partner owns the asset jointly with equal rights 
      and obligations. Upon the death of one or more of the joint tenants, the surviving joint owners receives all the 
      rights to the asset, so even if the deceased was hoping to pass the value of the property to designated heirs.`,
  },
  common: {
    title: 'Joint Tenants in Common',
    description: `Please consult your lawyer or investment advisor as to the type of joint investment you need as many
      jurisdictions have different rules, laws, and definitions of these terms. In general, in jurisdictions
      that recognize joint tenancy in common allows for percentage-based ownership and the assets don't
      automatically go to the surviving partner as with joint tenancy with right of survivorship. Instead, the
      joint tenancy in common allows the assets to get distributed as stipulated in each joint owners will as to
      his or her percentage.`,
  },
  entirety: {
    title: 'Tenants by the Entirety',
    description: `Please consult your lawyer or investment advisor as to the type of joint investment you need as many
      jurisdictions have different rules, laws, and definitions of these terms. In general, in states that
      recognize Tenants by the Entirety, the term can mean a form of shared property ownership that is reserved
      only for married couples. A tenancy by the entirety generally permits spouses to jointly own property as a
      single legal entity. This means that each spouse has an equal and undivided interest in the property and
      generally creates a right of survivorship so if one spouse dies, the surviving spouse automatically
      receives full title to the asset.`,
  },
}

const JointlyOptions = (props: IFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [selected, setSelected] = useState('')
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [selectedInfo, setSelectedInfo] = useState('')

  const { updateOnboardProgress } = useRiseUpAPIV1()
  const sendDataToGTM = useGTMDispatch()
  const { errorMessage, setErrorMessage } = useErrorMessage()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()
  const navigate = useNavigate()
  const location = useLocation()

  const handleSelection = (e: any) => {
    setSelected(e.target.value)
    props.setValue('investingProfile.jointAccountType', e.target.value)
    props.setValue('investingProfile.accountType', getInvestorAccountType(e.target.value))
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    const data = {
      ...props.getValues(),
      lastCompletedStage: '3',
    }
    try {
      const updateOnboardProgressRes = await updateOnboardProgress(data)
      const formattedUTMParams = getFormattedUTMParams()
      const pathname = location.pathname
      const currentParams = queryString.parse(location.search)
      const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'investor_personal_info')

      if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
        sendDataToGTM({
          event: 'funnel_step',
          step_title: 'JOINT_TYPE_SELECTION',
          ...gtmData,
          ...formattedUTMParams,
        })
        navigate(updatedUrl)
        props.nextStep('3')
      }
      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      // catch the duplicate errors
      if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
        setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
        return
      }
      if (error.response.data === RiseUpErrors.DuplicateSSN) {
        setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
        return
      }
      swal({
        title: 'Error!',
        text: 'Please check if your details are valid and correct. If error persists, contact us.',
        icon: 'error',
      })
    }
  }

  const setInfoData = (key: string) => {
    switch (key) {
      case 'survivorship':
        return infoText.survivorship
      case 'common':
        return infoText.common
      case 'entirety':
        return infoText.entirety
      default:
        return { title: 'N/A', description: 'N/A' }
    }
  }

  useEffect(() => {
    if (props.getValues('investingProfile.jointAccountType')) {
      setSelected(props.getValues('investingProfile.jointAccountType'))
    }
  }, [props.getValues('investingProfile.jointAccountType')]) // eslint-disable-line

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side2} alt={`${Side2}`} />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        {/* <div className="justify-center mb-10 sm:hidden lg:flex">
          <div className="w-1/2">
            <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
          </div>
        </div> */}

        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
          Joint Account Type
        </div>

        <div className="mt-4">
          <div className="py-2 text-xl md:py-3 md:text-2xl">
            {/* <input
              {...props.registerField('investingProfile.jointAccountType', { required: true })}
              id="survivorship"
              type="radio"
              checked={selected === 'survivorship'}
              onChange={(e) => handleSelection(e)}
              onClick={(e) => handleSelection(e)}
              value={'survivorship'}
              className="hidden"
            /> */}
            <div className="hover: ml-3 flex h-10 cursor-pointer items-start lg:w-screen">
              <Checkbox
                label=""
                isChecked={selected === 'survivorship'}
                id="survivorship"
                value={'survivorship'}
                onChange={(e) => handleSelection(e)}
              />
              Joint Tenants with Right of Survivorship
              <span
                className="hover: mx-2 cursor-pointer py-1 text-lg"
                onClick={() => {
                  setShowInfoModal(true)
                  setSelectedInfo('survivorship')
                }}
              >
                <Tooltip title="click for more info">
                  <InfoCircleFilled className="text-gray-400" />
                </Tooltip>
              </span>
            </div>
          </div>

          <div className="py-2 text-xl md:py-3 md:text-2xl">
            {/* <input
              {...props.registerField('investingProfile.jointAccountType', { required: true })}
              id="common"
              type="radio"
              checked={selected === 'common'}
              onChange={(e) => handleSelection(e)}
              onClick={(e) => handleSelection(e)}
              value={'common'}
              className="hidden"
            /> */}
            <div className="hover: ml-3 flex h-10 cursor-pointer items-start lg:w-screen">
              <Checkbox
                label=""
                isChecked={selected === 'common'}
                id="common"
                value={'common'}
                onChange={(e) => handleSelection(e)}
              />
              Joint Tenants in Common
              <span
                className="hover: mx-2 cursor-pointer py-1 text-4xl text-lg"
                onClick={() => {
                  setShowInfoModal(true)
                  setSelectedInfo('common')
                }}
              >
                <Tooltip title="click for more info">
                  <InfoCircleFilled className="text-gray-400" />
                </Tooltip>
              </span>
            </div>
          </div>

          <div className="py-2 text-xl md:py-3 md:text-2xl">
            {/* <input
              {...props.registerField('investingProfile.jointAccountType', { required: true })}
              id="entirety"
              type="radio"
              checked={selected === 'entirety'}
              onChange={(e) => handleSelection(e)}
              onClick={(e) => handleSelection(e)}
              value={'entirety'}
              className="hidden"
            /> */}
            <div className="hover: ml-3 flex h-10 cursor-pointer items-start lg:w-screen">
              <Checkbox
                label=""
                isChecked={selected === 'entirety'}
                id="entirety"
                value={'entirety'}
                onChange={(e) => handleSelection(e)}
              />
              Tenants by the Entirety
              <span
                className="hover: mx-2 cursor-pointer py-1 text-lg"
                onClick={() => {
                  setShowInfoModal(true)
                  setSelectedInfo('entirety')
                }}
              >
                <Tooltip title="click for more info">
                  <InfoCircleFilled className="text-gray-400" />
                </Tooltip>
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 text-4xl text-gray-500 lg:text-base  lg:leading-5" hidden>
          {props.getValues('investingProfile.jointAccountType') === 'survivorship' ? (
            <div>
              Please consult your lawyer or investment advisor as to the type of joint investment you need as many
              jurisdictions have different rules, laws, and definitions of these terms. In general, in states that
              recognize Joint Tenants with Right of Survivorship, the term can mean each partner owns the asset jointly
              with equal rights and obligations. Upon the death of one or more of the joint tenants, the surviving joint
              owners receives all the rights to the asset, so even if the deceased was hoping to pass the value of the
              property to designated heirs.
            </div>
          ) : null}

          {props.getValues('investingProfile.jointAccountType') === 'common' ? (
            <div>
              Please consult your lawyer or investment advisor as to the type of joint investment you need as many
              jurisdictions have different rules, laws, and definitions of these terms. In general, in jurisdictions
              that recognize joint tenancy in common allows for percentage-based ownership and the assets don't
              automatically go to the surviving partner as with joint tenancy with right of survivorship. Instead, the
              joint tenancy in common allows the assets to get distributed as stipulated in each joint owners will as to
              his or her percentage.
            </div>
          ) : null}

          {props.getValues('investingProfile.jointAccountType') === 'entirety' ? (
            <div>
              Please consult your lawyer or investment advisor as to the type of joint investment you need as many
              jurisdictions have different rules, laws, and definitions of these terms. In general, in states that
              recognize Tenants by the Entirety, the term can mean a form of shared property ownership that is reserved
              only for married couples. A tenancy by the entirety generally permits spouses to jointly own property as a
              single legal entity. This means that each spouse has an equal and undivided interest in the property and
              generally creates a right of survivorship so if one spouse dies, the surviving spouse automatically
              receives full title to the asset.
            </div>
          ) : null}
        </div>

        <div className="w-full justify-center gap-3 py-10 sm:block lg:flex">
          <div className="flex flex-col sm:w-[100%] lg:w-[20%]">
            <Button
              btnName={'Back'}
              onClickHandler={() => {
                props.previousStep('2')
              }}
            />
          </div>
          <div className="flex flex-col sm:w-[100%] lg:w-[80%]">
            <Button
              btnName={'Next: Investor Info (Joint Investors)'}
              btnType={'button'}
              onClickHandler={handleSubmit}
              disabled={props.getValues('investingProfile.jointAccountType') === undefined}
            />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <InfoModal
        onCancel={() => setShowInfoModal(false)}
        isVisible={showInfoModal}
        onOk={() => setShowInfoModal(false)}
        title={selectedInfo && setInfoData(selectedInfo).title}
        description={selectedInfo && setInfoData(selectedInfo).description}
      />
      <MessageModal
        isVisible={errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
          }
          setErrorMessage('')
        }}
        onCancel={() => {
          setErrorMessage('')
        }}
        title={'Error'}
        description={errorMessage}
        type={'error'}
        btn2={'Ok'}
      />
    </div>
  )
}

export default JointlyOptions
